<template>
  <TsSection container-class="max-md:px-0">
    <TsCard
      append-class="bg-white max-w-[1248px] py-8 px-4 md:px-20 mb-8 rounded-lg transform duration-700"
      no-shadow
      no-border
    >
      <div class="flex flex-col md:flex-row justify-between gap-3 max-md:mb-3">
        <div class="flex flex-col gap-y-3">
          <TsTypography
            append-class="max-md:text-center text-idle-black font-bold text-xl md:text-2xl"
            :text="props.title"
          />
          <TsCard no-border no-shadow append-class="hidden md:block">
            <TsTypography
              append-class="text-natural-grey font-medium leading-5 text-sm max-md:text-center max-w-[300px]"
              :text="props.description"
            />
          </TsCard>
        </div>

        <div class="flex-col md:flex-row max-md:items-center flex max-md:gap-2 md:space-x-16">
          <div class="flex md:mr-14">
            <div v-for="(unit, index) in timeUnits" :key="unit?.label" class="flex">
              <div class="flex flex-col items-center gap-y-1">
                <div
                  :data-testid="`${dataTestid}-${unit.label}-box`"
                  class="flex items-center justify-center py-4 px-3 border border-natural-light-grey bg-white rounded-lg w-12"
                >
                  <TsTypography
                    variant="h2"
                    align="center"
                    append-class="text-idle-black leading-4 !mb-0"
                    :data-testid="`${dataTestid}-${unit.label}-value`"
                  >
                    {{ unit.value.value.toString().padStart(2, '0') }}
                  </TsTypography>
                </div>

                <TsTypography
                  align="center"
                  variant="h6"
                  weight="semibold"
                  append-class="leading-4 text-natural-grey"
                  :data-testid="`${dataTestid}-${unit.label}-label`"
                >
                  {{ unit.label }}
                </TsTypography>
              </div>

              <span v-if="index < timeUnits.length - 1" class="font-bold mx-1 mt-3">:</span>
            </div>
          </div>
        </div>
        <TsCard no-border no-shadow append-class="block md:hidden">
          <TsTypography
            append-class="text-natural-grey font-medium leading-5 text-sm text-center max-md:text-center"
            :text="props.description"
          />
        </TsCard>
      </div>
    </TsCard>
  </TsSection>
</template>

<script setup lang="ts">
const TsCard = defineAsyncComponent(() => import('~/components/organisms/TsCard.vue'));

const { isMobile: _isMobile } = useDevice();

type Props = {
  title?: string;
  description?: string;
  link: string;
  targetDate?: number;
  dataTestid: string;
};

const props = withDefaults(defineProps<Props>(), {
  title: "Don't Miss Out! Limited Time Deal!",
  description: 'Get your Dewalt Drill Machine for 30% off! Limited stock—grab yours now!',
  targetDate: 1729043763853,
});

const timeLeft = ref(0);
const interval = ref<ReturnType<typeof setInterval> | null>(null);
const targetDate = ref(new Date(props.targetDate).getTime()); // Now Date is hardcoded, Need to change from Builder

const calculateTimeLeft = () => {
  const now = new Date().getTime();
  timeLeft.value = targetDate.value - now;

  if (timeLeft.value < 0) {
    clearInterval(interval.value!);
    timeLeft.value = 0;
  }
};

const days = computed(() => Math.floor(timeLeft.value / (1000 * 60 * 60 * 24)));
const hours = computed(() =>
  Math.floor((timeLeft.value % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
);
const minutes = computed(() => Math.floor((timeLeft.value % (1000 * 60 * 60)) / (1000 * 60)));
const seconds = computed(() => Math.floor((timeLeft.value % (1000 * 60)) / 1000));

const timeUnits = computed(() => [
  { value: days, label: useTranslation('days', 'Days') },
  { value: hours, label: useTranslation('hours', 'Hours') },
  { value: minutes, label: useTranslation('mins', 'Mins') },
  { value: seconds, label: useTranslation('secs', 'Secs') },
]);

onMounted(() => {
  calculateTimeLeft();
  interval.value = setInterval(calculateTimeLeft, 1000);
});

onBeforeUnmount(() => {
  if (interval.value) {
    clearInterval(interval.value);
  }
});
</script>
